import { useRef, useEffect } from 'react';
import { isEqual } from 'lodash-es';
import {
  useDataSourceStore,
  useDataSourceStoreSelector,
} from '../../state/data-source/data-source-store-provider';
import type { DataSource } from '../../types/data-source';
import { isMergedDataSourceValue } from '../../utils/get-mapped-data-source/get-merged-data-source-value';

/**
 * This custom equality function only cares if the value of the data source
 * has changed. It may be totally fine to just check if the data source
 * is equal or not but, for now, this is more likely what we actually
 * want to be the trigger for updating the ref.
 */

export const areDataSourceValuesEqual = (
  prev: DataSource | undefined,
  next: DataSource | undefined,
) => {
  if (
    isMergedDataSourceValue(prev?.value) ||
    isMergedDataSourceValue(next?.value)
  ) {
    const prevBaseSourceValue = isMergedDataSourceValue(prev?.value)
      ? prev?.value.baseSource.value
      : undefined;
    const nextBaseSourceValue = isMergedDataSourceValue(next?.value)
      ? next?.value.baseSource.value
      : undefined;
    const prevMergeSourcesValue = isMergedDataSourceValue(prev?.value)
      ? prev?.value.mergeSources
      : undefined;
    const nextMergeSourcesValue = isMergedDataSourceValue(next?.value)
      ? next?.value.mergeSources
      : undefined;
    return (
      isEqual(prevBaseSourceValue, nextBaseSourceValue) &&
      isEqual(prevMergeSourcesValue, nextMergeSourcesValue)
    );
  }

  const dSourceValueIsEqual = isEqual(prev?.value, next?.value);
  return dSourceValueIsEqual;
};

/**
 * Allows a component to subscribe to changes to a specified data source.
 * @param dataSource - the name of the data source
 */

export const useDataSource = (dataSourceName: string | undefined) => {
  const dataSources = useDataSourceStoreSelector((state) => state.dataSources);
  const { subscribe } = useDataSourceStore();
  const dataSource = dataSourceName
    ? dataSources.get(dataSourceName)
    : undefined;

  /**
   * This ref + useEffect/subscribe approach is recommended by the [zustand docs](https://github.com/pmndrs/zustand?tab=readme-ov-file#transient-updates-for-often-occurring-state-changes)
   * and allows a consumer component to only receive new values when the data of its reference data source
   * updates (avoiding calling it every time the entity root data updates)
   */
  const dataSourceRef = useRef<DataSource | undefined>(dataSource ?? undefined);

  useEffect(() => {
    const unsubscribe = dataSourceName
      ? subscribe(
          /**
           * First argument is a selector. The returned value is used to track when the slice has updated.
           */
          (state) => state.dataSources.get(dataSourceName),
          /**
           * This is a callback that fires when the result of the selector does not match the previous value
           */
          (value) => {
            dataSourceRef.current = value;
          },
          {
            equalityFn: areDataSourceValuesEqual,
          },
        )
      : () => {};
    return () => {
      unsubscribe();
    };
  });

  return dataSourceRef.current;
};
